import { titlesByLocation } from "react-migration/domains/ownership";
import { useMemo } from "react";
import type { Position2D } from "@deck.gl/core";
import type { ClickedLatLng } from "react-migration/lib/map/typings";
import { cleanTypename } from "react-migration/lib/util/cleanTypename";
import { generateMergedPolygon } from "../../../../lib/util/generateMergedPolygon";
import { Geometry, Title } from "../../typings";
import { useTitlePolysByLocation } from "./useTitlePolysByLocation";

/**
 * Takes a clicked point and returns the first intersecting title polygon
 * @param clickedLatLng: { lat: number; lng: number; }
 * @returns QueryResult
 */
export function useSelectedTitlePolyQuery(clickedLatLng?: ClickedLatLng) {
  const coordinates = [clickedLatLng?.lng, clickedLatLng?.lat] as Position2D;

  const {
    titlesByLocation,
    loading,
    queryResult: { error },
  } = useTitlePolysByLocation({
    type: "Point",
    coordinates,
  });

  const defaultSanitizedPolygonData = {
    selectedPolygon: undefined,
    selectedPolygons: [],
  };
  const sanitizedPolygonData = useSanitizeTitlePolygon(titlesByLocation);
  const polygonData = error ? defaultSanitizedPolygonData : sanitizedPolygonData;

  return {
    loading,
    error,
    ...polygonData,
  };
}

const useSanitizeTitlePolygon = (titles: Title[]) => {
  const { selectedPolygons, mergedPolygon } = useMemo(() => sanitizeTitlePolygon(titles), [titles]);

  return { selectedPolygon: mergedPolygon, selectedPolygons };
};

function sanitizeTitlePolygon(titles: Title[]) {
  const selected = getCleanPolygons(titles);
  const merged = selected.length > 1 ? generateMergedPolygon(selected) : selected[0];

  return { selectedPolygons: selected, mergedPolygon: merged };
}

/**
 * Takes the title by location query data and returns a flat array of polygons
 * @param data: TitlesByLocationQueryData
 * @returns Geometry[]
 */
const getCleanPolygons = (titlesByLocation: Title[]) => {
  const titlePolygons = titlesByLocation.map((title) => title.polygons).flat();
  const geometries: (Geometry | undefined)[] = titlePolygons.map((polygon) =>
    cleanTypename(polygon?.geometry)
  );

  return geometries.filter((item: Geometry | undefined): item is Geometry => {
    return !!item;
  });
};
